/* ============================================
   SCP Pagination - Multi-fetch concat engine

   Why this exists
   ---------------
   Shopify theme Liquid cannot read arbitrary URL query parameters - the
   `request` object exposes only design_mode, host, locale, origin, page_type,
   path, visual_preview_mode (see https://shopify.dev/docs/api/liquid/objects/request).
   That means the server cannot honor `?per_page=24` to change the rendered
   page size; `{% paginate by N %}` is locked to a single Liquid value.

   The standard `?page=N` parameter IS auto-honored by `paginate`, both on
   normal renders and through Shopify's Section Rendering API. We exploit
   that: the merchant's smallest `per_page_option` is treated as the SERVER
   UNIT of pagination (serverPerPage). Any larger user choice is K *
   serverPerPage. To show client page M at userPerPage, we fetch server pages
   `(M-1)*K+1 ... M*K` in parallel via `fetchSection`, concatenate the
   `.m-product-item` nodes, and re-render the pagination bar client-side.

   Layout contract
   ---------------
   - section.settings.per_page_options must be a comma-separated list whose
     entries are integer multiples of the smallest entry (e.g. "12,24,48").
   - Liquid forces the section's `paginate by` to equal the smallest option,
     so serverPerPage == validOptions[0].
   - [data-product-container] carries data-server-per-page, data-total-items,
     data-current-per-page, data-current-page, data-per-page-options.

   Behavior summary
   ----------------
   - User picks per_page  -> applyPerPage(value, 1).
   - User clicks any pagination link -> applyPerPage(perPageFromHref, pageFromHref).
   - Browser back/forward -> popstate replays from URL.
   - Hard refresh on `?per_page=24&page=3` -> server renders server-page 3,
     JS detects the mismatch and swaps in the correct concat slice (cards
     49-72). Brief first-paint flicker is accepted.
   - Modifier-key / middle-click / target=_blank navigations are left to
     the browser so "open in new tab" still works.
   ============================================ */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.facest-filters-section';
  var CONTAINER_SELECTOR = '[data-product-container]';
  var PAGINATION_WRAPPER_SELECTOR = '.m-collection--pagination';
  var PAGINATION_NAV_SELECTOR = '.scp-pagination';
  var SELECT_SELECTOR = '[data-scp-per-page]';
  var EMPTY_STATE_SELECTOR = '.m-collection--empty';
  var I18N_SELECTOR = '[data-scp-pagination-i18n]';

  var ICONS = {
    first:
      '<svg class="scp-pagination__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<path d="M11 12L7 8l4-4M7 12L3 8l4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>',
    previous:
      '<svg class="scp-pagination__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<path d="M10 12L6 8l4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>',
    next:
      '<svg class="scp-pagination__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>',
    last:
      '<svg class="scp-pagination__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<path d="M5 4l4 4-4 4M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>',
  };

  var inFlight = false;
  var serverPageCache = {};
  var i18nCache = null;

  /* ---------- helpers ---------- */

  function getSection() {
    return document.querySelector(SECTION_SELECTOR);
  }

  function getContainer() {
    return document.querySelector(CONTAINER_SELECTOR);
  }

  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function escapeText(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function getI18n() {
    if (i18nCache) return i18nCache;
    var node = document.querySelector(I18N_SELECTOR);
    var fallback = {
      first: 'First page',
      previous: 'Previous page',
      next: 'Next page',
      last: 'Last page',
      page_n: 'Page __SCP_N__',
      showing_count: '__SCP_START__-__SCP_END__ of __SCP_TOTAL__',
      products_per_page: 'Products per page',
    };
    if (!node) return fallback;
    try {
      var parsed = JSON.parse(node.textContent || node.innerText || '{}');
      i18nCache = {
        first: parsed.first || fallback.first,
        previous: parsed.previous || fallback.previous,
        next: parsed.next || fallback.next,
        last: parsed.last || fallback.last,
        page_n: parsed.page_n || fallback.page_n,
        showing_count: parsed.showing_count || fallback.showing_count,
        products_per_page: parsed.products_per_page || fallback.products_per_page,
      };
      return i18nCache;
    } catch (err) {
      return fallback;
    }
  }

  function readState() {
    var section = getSection();
    var container = getContainer();
    if (!section || !container) return null;

    // Only the 'paginate' type uses a numbered pagination bar + per-page
    // picker. For 'loadmore' / 'infinite', Minimog's collection.js owns
    // pagination via fetchSection on a Load More button - leave it alone.
    if (section.dataset.paginationType && section.dataset.paginationType !== 'paginate') {
      return null;
    }

    var serverPerPage = parseInt(container.getAttribute('data-server-per-page'), 10);
    if (isNaN(serverPerPage) || serverPerPage <= 0) {
      // Fallback: data-current-per-page on first paint matches the server unit.
      serverPerPage = parseInt(container.getAttribute('data-current-per-page'), 10);
    }
    var totalItems = parseInt(container.getAttribute('data-total-items'), 10);
    if (isNaN(totalItems) || totalItems < 0) totalItems = 0;
    var totalServerPages = parseInt(container.getAttribute('data-total-pages'), 10);
    if (isNaN(totalServerPages) || totalServerPages < 1) {
      totalServerPages = serverPerPage > 0 ? Math.max(1, Math.ceil(totalItems / serverPerPage)) : 1;
    }
    var currentPage = parseInt(container.getAttribute('data-current-page'), 10) || 1;
    var currentPerPage = parseInt(container.getAttribute('data-current-per-page'), 10) || serverPerPage;
    var optionsRaw = container.getAttribute('data-per-page-options') || '';
    var options = optionsRaw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);

    return {
      sectionId: section.dataset.sectionId,
      serverPerPage: serverPerPage,
      totalItems: totalItems,
      totalServerPages: totalServerPages,
      currentPage: currentPage,
      currentPerPage: currentPerPage,
      options: options,
    };
  }

  function validOptions(state) {
    if (!state || !state.options || !state.options.length || !state.serverPerPage) return [];
    var unit = state.serverPerPage;
    var out = [];
    for (var i = 0; i < state.options.length; i++) {
      var n = parseInt(state.options[i], 10);
      if (!isNaN(n) && n > 0 && n % unit === 0) out.push(n);
    }
    return out;
  }

  function urlWith(perPage, page) {
    var url = new URL(window.location.href);
    if (perPage) url.searchParams.set('per_page', String(perPage));
    else url.searchParams.delete('per_page');
    if (page && Number(page) > 1) url.searchParams.set('page', String(page));
    else url.searchParams.delete('page');
    return url.toString();
  }

  function readUrlState() {
    var url;
    try { url = new URL(window.location.href); }
    catch (err) { return { perPage: null, page: 1 }; }
    var perPageStr = url.searchParams.get('per_page');
    var pageStr = url.searchParams.get('page');
    var perPage = perPageStr ? parseInt(perPageStr, 10) : null;
    var page = pageStr ? parseInt(pageStr, 10) : 1;
    if (isNaN(perPage)) perPage = null;
    if (isNaN(page) || page < 1) page = 1;
    return { perPage: perPage, page: page };
  }

  function scrollToGrid() {
    var grid = document.getElementById('CollectionProductGrid');
    if (!grid) return;
    try {
      var top = grid.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: top, behavior: 'smooth' });
    } catch (err) {
      window.scrollTo(0, grid.offsetTop - 80);
    }
  }

  /* ---------- pagination bar generator ---------- */

  /**
   * Compute the list of page numbers + ellipses for a Shopify-style bar.
   * Always shows: 1, last, current, current-1, current+1.
   * Inserts -1 sentinel where an ellipsis should appear.
   *
   * Examples (page=5, pages=10):
   *   [1, -1, 4, 5, 6, -1, 10]
   * Examples (page=1, pages=5):
   *   [1, 2, 3, 4, 5]
   */
  function computePageParts(currentPage, pages) {
    if (pages <= 1) return [1];
    var nums = {};
    nums[1] = true;
    nums[pages] = true;
    nums[currentPage] = true;
    if (currentPage - 1 >= 1) nums[currentPage - 1] = true;
    if (currentPage + 1 <= pages) nums[currentPage + 1] = true;

    var sorted = Object.keys(nums).map(function (k) { return parseInt(k, 10); }).sort(function (a, b) { return a - b; });
    var out = [];
    for (var i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push(-1);
      out.push(sorted[i]);
    }
    return out;
  }

  function urlForPage(perPage, page) {
    var url = new URL(window.location.href);
    if (perPage) url.searchParams.set('per_page', String(perPage));
    if (page && Number(page) > 1) url.searchParams.set('page', String(page));
    else url.searchParams.delete('page');
    return url.pathname + url.search + url.hash;
  }

  function buildPaginationBar(perPage, page, totalItems, options) {
    var i18n = getI18n();
    var pages = perPage > 0 ? Math.max(1, Math.ceil(totalItems / perPage)) : 1;
    if (page > pages) page = pages;
    if (page < 1) page = 1;

    var html = '<ul class="scp-pagination__pages">';

    // First page
    if (page > 1) {
      html += '<li><a href="' + escapeAttr(urlForPage(perPage, 1)) +
        '" class="scp-pagination__control" aria-label="' + escapeAttr(i18n.first) + '">' + ICONS.first + '</a></li>';
    } else {
      html += '<li><span class="scp-pagination__control is-disabled" aria-hidden="true">' + ICONS.first + '</span></li>';
    }

    // Previous
    if (page > 1) {
      html += '<li><a href="' + escapeAttr(urlForPage(perPage, page - 1)) +
        '" class="scp-pagination__control" aria-label="' + escapeAttr(i18n.previous) + '">' + ICONS.previous + '</a></li>';
    } else {
      html += '<li><span class="scp-pagination__control is-disabled" aria-hidden="true">' + ICONS.previous + '</span></li>';
    }

    // Page numbers
    var parts = computePageParts(page, pages);
    for (var i = 0; i < parts.length; i++) {
      var n = parts[i];
      if (n === -1) {
        html += '<li><span class="scp-pagination__page scp-pagination__page--ellipsis" aria-hidden="true">&hellip;</span></li>';
      } else if (n === page) {
        html += '<li><span class="scp-pagination__page scp-pagination__page--active" aria-current="page">' + n + '</span></li>';
      } else {
        var label = i18n.page_n.replace('__SCP_N__', n);
        html += '<li><a href="' + escapeAttr(urlForPage(perPage, n)) +
          '" class="scp-pagination__page" aria-label="' + escapeAttr(label) + '">' + n + '</a></li>';
      }
    }

    // Next
    if (page < pages) {
      html += '<li><a href="' + escapeAttr(urlForPage(perPage, page + 1)) +
        '" class="scp-pagination__control" aria-label="' + escapeAttr(i18n.next) + '">' + ICONS.next + '</a></li>';
    } else {
      html += '<li><span class="scp-pagination__control is-disabled" aria-hidden="true">' + ICONS.next + '</span></li>';
    }

    // Last
    if (page < pages) {
      html += '<li><a href="' + escapeAttr(urlForPage(perPage, pages)) +
        '" class="scp-pagination__control" aria-label="' + escapeAttr(i18n.last) + '">' + ICONS.last + '</a></li>';
    } else {
      html += '<li><span class="scp-pagination__control is-disabled" aria-hidden="true">' + ICONS.last + '</span></li>';
    }

    html += '</ul><div class="scp-pagination__meta">';

    // Per-page select
    if (options && options.length > 1) {
      html += '<label class="scp-pagination__per-page">' +
        '<span class="scp-pagination__per-page-label">' + escapeText(i18n.products_per_page) + '</span>' +
        '<select class="scp-pagination__per-page-select" data-scp-per-page aria-label="' + escapeAttr(i18n.products_per_page) + '">';
      for (var j = 0; j < options.length; j++) {
        var opt = options[j];
        var isSel = parseInt(opt, 10) === perPage ? ' selected' : '';
        html += '<option value="' + escapeAttr(opt) + '"' + isSel + '>' + escapeText(opt) + '</option>';
      }
      html += '</select></label>';
    }

    // Count
    if (totalItems > 0) {
      var startIdx = (page - 1) * perPage + 1;
      var endIdx = Math.min(page * perPage, totalItems);
      var countText = i18n.showing_count
        .replace('__SCP_START__', startIdx)
        .replace('__SCP_END__', endIdx)
        .replace('__SCP_TOTAL__', totalItems);
      html += '<span class="scp-pagination__count">' + escapeText(countText) + '</span>';
    }

    html += '</div>';
    return html;
  }

  /* ---------- core engine ---------- */

  function setLoading(loading) {
    var container = getContainer();
    if (container) container.classList[loading ? 'add' : 'remove']('m-spinner-loading');
  }

  /**
   * Concatenate the children of [data-product-container] from each fetched
   * server-page DOM into a fresh DocumentFragment.
   */
  function concatChildren(serverPageDocs) {
    var frag = document.createDocumentFragment();
    for (var i = 0; i < serverPageDocs.length; i++) {
      var doc = serverPageDocs[i];
      if (!doc) continue;
      var src = doc.querySelector(CONTAINER_SELECTOR);
      if (!src) continue;
      var nodes = src.children;
      // Deep-clone instead of moving so the cached doc survives reuse.
      // appendChild on a live node would yank it out of the cache, leaving
      // an empty container on the next hit (e.g. picking 24 then 12 again).
      for (var j = 0; j < nodes.length; j++) frag.appendChild(nodes[j].cloneNode(true));
    }
    return frag;
  }

  function fetchServerPage(state, pageNum) {
    if (serverPageCache[pageNum]) {
      return Promise.resolve(serverPageCache[pageNum]);
    }
    if (typeof fetchSection !== 'function') {
      return Promise.reject(new Error('fetchSection helper unavailable'));
    }
    return fetchSection(state.sectionId, { params: { page: pageNum } })
      .then(function (doc) {
        serverPageCache[pageNum] = doc;
        return doc;
      });
  }

  function rebindMinimog() {
    if (typeof MinimogEvents !== 'undefined' && MinimogEvents.emit) {
      MinimogEvents.emit('ON_PRODUCT_LIST_UPDATED');
    }
    if (typeof MinimogTheme !== 'undefined') {
      if (MinimogTheme.Wishlist && typeof MinimogTheme.Wishlist.setWishlistButtonsState === 'function') {
        MinimogTheme.Wishlist.setWishlistButtonsState();
      }
      if (MinimogTheme.CompareProduct && typeof MinimogTheme.CompareProduct.setCompareButtonsState === 'function') {
        MinimogTheme.CompareProduct.setCompareButtonsState();
      }
    }
  }

  /**
   * Re-render the grid + pagination bar at (perPage, page).
   *
   * @param {number} perPage   target client per_page
   * @param {number} page      target client page (1-based)
   * @param {object} opts
   * @param {string} opts.history  'push' (default) | 'replace' | 'none'
   * @param {boolean} opts.scroll  scroll grid into view (default true)
   */
  function applyPerPage(perPage, page, opts) {
    if (inFlight) return;

    opts = opts || {};
    var historyMode = opts.history || 'push';
    var doScroll = opts.scroll !== false;

    var state = readState();
    if (!state) return;

    var allowed = validOptions(state);
    perPage = parseInt(perPage, 10);
    if (isNaN(perPage) || allowed.indexOf(perPage) === -1) return;

    page = parseInt(page, 10);
    if (isNaN(page) || page < 1) page = 1;

    var totalClientPages = Math.max(1, Math.ceil(state.totalItems / perPage));
    if (page > totalClientPages) page = totalClientPages;

    var K = perPage / state.serverPerPage;
    var startServerPage = (page - 1) * K + 1;
    var endServerPage = Math.min(startServerPage + K - 1, state.totalServerPages);

    var fetches = [];
    for (var n = startServerPage; n <= endServerPage; n++) fetches.push(fetchServerPage(state, n));

    inFlight = true;
    setLoading(true);

    Promise.all(fetches)
      .then(function (docs) {
        var container = getContainer();
        if (!container) return;

        var frag = concatChildren(docs);

        // Empty existing children, then append concatenated nodes.
        while (container.firstChild) container.removeChild(container.firstChild);
        container.appendChild(frag);

        // Update container metadata so subsequent reads stay accurate.
        container.setAttribute('data-current-per-page', String(perPage));
        container.setAttribute('data-current-page', String(page));

        // Replace pagination bar inside the wrapper, preserving the i18n
        // bootstrap script so future re-renders can still read translations
        // from the DOM if the in-memory cache ever resets.
        var nav = document.querySelector(PAGINATION_NAV_SELECTOR);
        if (nav) {
          var i18nScript = nav.querySelector(I18N_SELECTOR);
          nav.innerHTML = buildPaginationBar(perPage, page, state.totalItems, state.options);
          if (i18nScript) nav.appendChild(i18nScript);
        } else {
          // No pagination bar in the DOM (e.g. paginate_type != 'paginate' or
          // single-page render). If we now have multiple client pages, inject
          // one inside the wrapper.
          var wrapper = document.querySelector(PAGINATION_WRAPPER_SELECTOR);
          if (wrapper && totalClientPages > 1) {
            var navEl = document.createElement('nav');
            navEl.className = 'scp-pagination';
            navEl.setAttribute('role', 'navigation');
            navEl.setAttribute('aria-label', 'Pagination');
            navEl.innerHTML = buildPaginationBar(perPage, page, state.totalItems, state.options);
            wrapper.appendChild(navEl);
          }
        }

        if (historyMode === 'push') {
          window.history.pushState({ scpPerPage: perPage, scpPage: page }, '', urlWith(perPage, page));
        } else if (historyMode === 'replace') {
          window.history.replaceState({ scpPerPage: perPage, scpPage: page }, '', urlWith(perPage, page));
        }

        rebindMinimog();
        document.dispatchEvent(new CustomEvent('scp:per-page-rendered', {
          detail: { perPage: perPage, page: page, totalItems: state.totalItems },
        }));

        if (doScroll) scrollToGrid();
      })
      .catch(function (err) {
        console.error('[scp-pagination] concat fetch failed', err);
      })
      .then(function () {
        inFlight = false;
        setLoading(false);
      });
  }

  /* ---------- listeners ---------- */

  function handleSelectChange(event) {
    var select = event.target.closest && event.target.closest(SELECT_SELECTOR);
    if (!select) return;
    var value = parseInt(select.value, 10);
    if (isNaN(value)) return;
    var state = readState();
    if (!state) return;
    if (validOptions(state).indexOf(value) === -1) return;
    if (value === state.currentPerPage && state.currentPage === 1) return;
    applyPerPage(value, 1, { history: 'push', scroll: true });
  }

  function handlePaginationClick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== undefined && event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target.closest && event.target.closest('a.scp-pagination__page, a.scp-pagination__control');
    if (!link) return;
    if (link.target && link.target !== '' && link.target !== '_self') return;

    var href = link.getAttribute('href');
    if (!href) return;

    var url;
    try { url = new URL(href, window.location.href); }
    catch (err) { return; }

    if (url.origin !== window.location.origin) return;
    if (url.pathname !== window.location.pathname) return;

    var state = readState();
    if (!state) return;

    var perPageStr = url.searchParams.get('per_page');
    var pageStr = url.searchParams.get('page');
    var perPage = perPageStr ? parseInt(perPageStr, 10) : state.currentPerPage;
    var page = pageStr ? parseInt(pageStr, 10) : 1;

    if (isNaN(perPage) || validOptions(state).indexOf(perPage) === -1) return;

    event.preventDefault();
    applyPerPage(perPage, page, { history: 'push', scroll: true });
  }

  function handlePopState() {
    var state = readState();
    if (!state) return;
    var url = readUrlState();
    var perPage = url.perPage;
    var page = url.page;

    if (perPage === null || validOptions(state).indexOf(perPage) === -1) {
      perPage = state.serverPerPage;
    }

    if (perPage === state.currentPerPage && page === state.currentPage) return;

    applyPerPage(perPage, page, { history: 'none', scroll: false });
  }

  /**
   * Reconcile the rendered grid with the URL on initial load:
   *  - If `?per_page=` is invalid (not in validOptions), strip from address bar.
   *  - If the URL state differs from what the server painted, run one swap.
   */
  function reconcileFromUrl() {
    var state = readState();
    if (!state) return;
    var url = readUrlState();
    var allowed = validOptions(state);

    if (url.perPage !== null && allowed.indexOf(url.perPage) === -1) {
      try {
        var cleaned = new URL(window.location.href);
        cleaned.searchParams.delete('per_page');
        window.history.replaceState({}, '', cleaned.toString());
      } catch (err) {
        /* ignore */
      }
      return;
    }

    if (url.perPage === null) return;
    if (url.perPage === state.currentPerPage && url.page === state.currentPage) return;

    applyPerPage(url.perPage, url.page, { history: 'replace', scroll: false });
  }

  /* ---------- bootstrap ---------- */

  document.addEventListener('change', handleSelectChange);
  document.addEventListener('click', handlePaginationClick);
  window.addEventListener('popstate', handlePopState);

  // Filter rerenders replace the entire .facest-filters-section with a fresh
  // copy that resets per_page to default. Clear the cache so subsequent
  // dropdown/page interactions don't reuse stale fetched data.
  document.addEventListener('collection:rerendered', function () {
    serverPageCache = {};
    i18nCache = null;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileFromUrl);
  } else {
    reconcileFromUrl();
  }
})();
